'use client'

/**
 * The face of an AI Coworker (plan 08 WP-VIS-05), ported from Driver-Auto-CC
 * `components/shell/CoworkerAvatar.tsx`.
 *
 * Monogram initials on a brand tone by default ("Matching Agent" -> "MA"). A
 * photo is shown only when a `photo` name is passed and the file loads from
 * `public/coworkers/<photo>.jpg`; a missing or broken photo falls back to the
 * monogram. Names come from the caller (API data), never from this file.
 */

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { initialsFor, toneIndexFor } from '@/lib/monogram'

const TONES = ['bg-brand-navy', 'bg-brand-purple', 'bg-brand-navy-light', 'bg-primary'] as const

export interface CoworkerAvatarProps {
  /** The coworker's display name, used for the monogram and the default alt text. */
  name: string
  /** File name in public/coworkers without the extension. Omit for the monogram. */
  photo?: string | null
  /** Pixel size of the square: 20 in the header pill, 32 in chat, 48 on a card. */
  size?: number
  /** A soft ring for the coworker that is working right now. */
  active?: boolean
  /**
   * Alt text. Defaults to the name; pass "" when the name is printed next to the
   * face, so screen readers do not hear it twice (Rule 14).
   */
  alt?: string
  className?: string
}

export function CoworkerAvatar({
  name,
  photo,
  size = 40,
  active = false,
  alt,
  className,
}: CoworkerAvatarProps) {
  const [failed, setFailed] = useState(false)
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  const label = alt ?? name
  const initials = initialsFor(name)
  const showPhoto = Boolean(photo) && !failed
  // Two letters need a smaller glyph than one to stay inside the circle.
  const fontScale = initials.length > 1 ? 0.36 : 0.42

  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
      title={label || undefined}
    >
      {active && (
        <span
          aria-hidden='true'
          className='absolute -inset-0.5 rounded-full ring-2 ring-brand-cornflower/60'
        />
      )}
      {showPhoto ? (
        // A small square from /public: next/image would add a loader and buy nothing.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={`${basePath}/coworkers/${photo}.jpg`}
          alt={label}
          width={size}
          height={size}
          onError={() => setFailed(true)}
          className='relative rounded-full object-cover ring-1 ring-hairline'
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          role={label ? 'img' : undefined}
          aria-label={label || undefined}
          aria-hidden={label ? undefined : true}
          className={cn(
            'relative flex select-none items-center justify-center rounded-full font-display font-semibold text-white ring-1 ring-hairline',
            TONES[toneIndexFor(name, TONES.length)]
          )}
          style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * fontScale)) }}
        >
          {initials}
        </span>
      )}
    </span>
  )
}
